import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import { leasingApi } from '@/utils/leasingApi';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Copy, ExternalLink, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_PILL = {
  draft:     'bg-amber-100 text-amber-800',
  published: 'bg-emerald-100 text-emerald-800',
  leased:    'bg-slate-200 text-slate-700',
};

export default function Listings() {
  const [rows, setRows] = useState([]);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    property_id: '', title_en: '', title_ar: '', description_en: '',
    monthly_rent_sar: 0, bedrooms: 0, bathrooms: 0, size_sqm: 0,
    city: 'Riyadh', district: '', amenities: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await leasingApi.listListings();
      setRows(r.data || []);
    } catch (e) { toast.error('Failed to load listings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.getProperties().then(r => setProps(r.data || [])).catch(() => {});
  }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await leasingApi.createListing({ ...form, monthly_rent_sar: Number(form.monthly_rent_sar) });
      toast.success('Listing created — publish it to make public');
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Create failed'); }
  };

  const onPublish = async (l) => {
    try {
      const r = await leasingApi.publishListing(l.id);
      toast.success(`Published · /l/${r.data.slug}`);
      load();
    } catch (e) { toast.error('Publish failed'); }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/l/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Public URL copied');
  };

  const onDelete = async (l) => {
    if (!window.confirm(`Delete listing "${l.title_en}"?`)) return;
    await leasingApi.deleteListing(l.id);
    toast.success('Deleted'); load();
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="listings-page">
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-[#0E1320]">Listings</h1>
            <p className="text-[13px] text-[#71777E] mt-1">Public-facing inventory · auto-generated landing pages · lead capture feeds Prospects.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="listings-add"><Plus className="mr-2 h-4 w-4" /> Add listing</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New listing</DialogTitle></DialogHeader>
              <form onSubmit={onCreate} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                <div><Label>Property *</Label>
                  <select required value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} className="w-full h-9 px-2 border rounded-md" data-testid="listing-property">
                    <option value="">Select property…</option>
                    {props.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Title (EN) *</Label><Input required value={form.title_en} onChange={e => setForm({ ...form, title_en: e.target.value })} data-testid="listing-title-en" /></div>
                  <div><Label>Title (AR)</Label><Input value={form.title_ar} onChange={e => setForm({ ...form, title_ar: e.target.value })} dir="rtl" /></div>
                </div>
                <div><Label>Description (EN)</Label>
                  <textarea value={form.description_en} onChange={e => setForm({ ...form, description_en: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded-md text-sm" />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><Label>Rent (SAR/mo) *</Label><Input type="number" required value={form.monthly_rent_sar} onChange={e => setForm({ ...form, monthly_rent_sar: e.target.value })} data-testid="listing-rent" /></div>
                  <div><Label>Beds</Label><Input type="number" value={form.bedrooms} onChange={e => setForm({ ...form, bedrooms: Number(e.target.value) })} /></div>
                  <div><Label>Baths</Label><Input type="number" value={form.bathrooms} onChange={e => setForm({ ...form, bathrooms: Number(e.target.value) })} /></div>
                  <div><Label>Size (sqm)</Label><Input type="number" value={form.size_sqm} onChange={e => setForm({ ...form, size_sqm: Number(e.target.value) })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>District</Label><Input value={form.district} onChange={e => setForm({ ...form, district: e.target.value })} /></div>
                </div>
                <Button type="submit" className="w-full">Create</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="rounded-xl border border-[#E6EAF0] bg-white overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FBFCFD] border-b border-[#ECEFF3]">
              <tr>
                {['Title', 'Rent', 'Beds', 'Status', 'Views', 'Leads', 'Public URL', ''].map(h => (
                  <th key={h} className="text-start px-3 py-2.5 text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E] font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#71777E]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#71777E]">No listings yet. Click + Add Listing to begin.</td></tr>
              ) : rows.map(r => (
                <tr key={r.id} data-testid={`listing-row-${r.id}`} className="border-b border-[#F1F4F7] hover:bg-[#F7FBF9]">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-[#0E1320]">{r.title_en}</div>
                    {r.title_ar && <div className="text-[11px] text-[#71777E]" dir="rtl">{r.title_ar}</div>}
                  </td>
                  <td className="px-3 py-2.5 reva-mono">SAR {(r.monthly_rent_sar || 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5">{r.bedrooms}br / {r.bathrooms}ba</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium reva-mono ${STATUS_PILL[r.status] || ''}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2.5 reva-mono text-[#4A5263]">{r.view_count}</td>
                  <td className="px-3 py-2.5 reva-mono text-[#00734A] font-semibold">{r.lead_count}</td>
                  <td className="px-3 py-2.5 text-[11px] reva-mono text-[#71777E]">
                    {r.status === 'published' ? `/l/${r.slug}` : <span className="opacity-50">— not published —</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      {r.status === 'draft' ? (
                        <Button size="sm" variant="outline" onClick={() => onPublish(r)} data-testid={`listing-publish-${r.id}`}>
                          <Send size={12} className="mr-1" /> Publish
                        </Button>
                      ) : (
                        <>
                          <button onClick={() => copyUrl(r.slug)} className="p-1.5 rounded hover:bg-[#F4F6F9]" title="Copy URL" data-testid={`listing-copy-${r.id}`}><Copy size={13} /></button>
                          <a href={`/l/${r.slug}`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-[#F4F6F9]" title="Open public page" data-testid={`listing-open-${r.id}`}><ExternalLink size={13} /></a>
                        </>
                      )}
                      <button onClick={() => onDelete(r)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete" data-testid={`listing-delete-${r.id}`}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
